import { logDataDogEvent } from 'utils/datadog';

/**
 * The click-to-verify token travels in the URL **fragment**, never the query string.
 *
 * A fragment is stripped by the user agent before the request is composed (RFC 3986 §3.5), so it
 * never appears in a request line — which is what keeps it out of the nginx access log and out of
 * Datadog's `@http.url`, where verify-identity URLs are otherwise searchable in plaintext. It also
 * means a mail-client link scanner (Outlook Safe Links, Gmail's proxy, corporate gateways) that
 * fetches the URL transmits no credential at all.
 *
 * See `lifemd:docs/specs/verify-identity-click-to-verify-link.md` for the full rationale and the
 * backend contract.
 */
export const VERIFY_LINK_FRAGMENT_KEY = 'vt';

/**
 * Reads the token out of a `#vt=…` fragment. Returns `''` when there is no token.
 *
 * Accepts a nullable hash because `window.location` is routinely stubbed in tests and not every
 * stub carries one — and a missing fragment is the overwhelmingly common case in production too,
 * since every patient who reaches this page any other way has none.
 */
/**
 * Reads the token out of a `?vt=…` query string, for links that reached us mangled.
 *
 * The fragment is the only shape we send, but it is not the only shape that arrives: a link
 * rewriter can percent-encode the `#`, and a templating mistake can emit `?` for it. Those used to
 * read as "no token at all" — capture returned `''`, the fragment was never cleared, the arrival
 * was never recorded, and the automatic request then deleted the code in the patient's email.
 *
 * Recovering the token here cannot undo the real damage of a query-string credential: by the time
 * any of this runs the value has already been in a request line and in our access logs. What it
 * can still do is record the arrival, take the value out of the address bar before the vendor
 * scripts read it, and let the exchange proceed instead of destroying the fallback as well.
 */
export const readVerifyLinkTokenFromQuery = (search: string | null | undefined): string =>
  readVerifyLinkToken(search?.startsWith('?') ? search.slice(1) : search);

export const readVerifyLinkToken = (hash: string | null | undefined): string => {
  if (!hash) return '';
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return '';
  try {
    return new URLSearchParams(raw).get(VERIFY_LINK_FRAGMENT_KEY)?.trim() ?? '';
  } catch {
    return '';
  }
};

/**
 * Drops the fragment from the address bar without adding a history entry.
 *
 * Called once the token has been read into component state. The token is single-use and short-lived
 * — the server sets the window and is the only thing that knows it — but there is no reason to
 * leave a live credential sitting in the address bar, in session history, or in whatever syncs it.
 *
 * A reload after this point lands on the ordinary code-entry form, which still works — but only
 * because `VERIFY_LINK_ARRIVAL_KEY` outlives the fragment. See `markVerifyLinkArrival`.
 */
export const clearVerifyLinkFragment = (): void => {
  if (typeof window.history?.replaceState !== 'function') return;
  const { pathname, search } = window.location;
  if (typeof pathname !== 'string') return;
  window.history.replaceState(null, '', `${pathname}${search ?? ''}`);
};

/**
 * Records, for the lifetime of the tab, that this page was reached on a link.
 *
 * The fact has to outlive the fragment, and module state does not. Clearing `#vt=` leaves a URL
 * that still carries `brazeExternalId` — every link email has one — so a reload reaches
 * `/verify-identity?brazeExternalId=…` with no token: no token means `linkPhase` starts at `none`,
 * and `none` is the one phase that lets the auto-request effect fire. Past the backend's 60-second
 * duplicate window, `request-code` deletes the pending row and issues a new code, which is the
 * exact harm `onUseCodeInstead` exists to avoid — the code in the email the patient is reading
 * stops working, and the only notice they get is that their typing is rejected.
 *
 * `sessionStorage` is the right lifetime rather than a convenient one: per tab, surviving reloads,
 * gone when the tab is. Stripping `brazeExternalId` from the URL instead would reach the same end
 * by breaking the flow that needs it — the page keys validate and resend off that value.
 *
 * Storage access throws in enough real configurations (Safari private mode, third-party-context
 * iframes, enterprise policy) that it cannot be allowed to take the page down. A throw costs the
 * protection and nothing else.
 */
// Also written, as a string literal, by the inline script at the top of `index.html`. If this
// value changes, change it there too — a mismatch is silent and costs the patient their code.
export const VERIFY_LINK_ARRIVAL_KEY = 'verify-identity:arrived-via-link';

/**
 * The value is the `brazeExternalId` the arrival was recorded for, not a bare `'1'`.
 *
 * A bare flag answers "has this tab ever seen a link", which is not the question the auto-request
 * gate is asking. A long-lived tab or WebView shell that handled a link once would go on
 * suppressing the automatic request for every later `/verify-identity?brazeExternalId=…` visit —
 * and that page says "We sent a 6-digit code to your email on file" either way, so the patient
 * waits for an email nobody sent. Keying it means the suppression only applies to the arrival it
 * was actually recorded for.
 *
 * A link with no `brazeExternalId` stores the empty string and matches a visit with none, which
 * is the same pairing one level down.
 */
const arrivalValue = (brazeExternalId: string): string => brazeExternalId;

/**
 * Reports a storage refusal once, without a value.
 *
 * These `catch` blocks are not inert. Falling back silently lands on exactly the behaviour the
 * flag exists to prevent — the automatic request fires and deletes the code in the patient's
 * email — so a configuration where storage throws (Safari private mode, a third-party-context
 * iframe, enterprise policy) should be visible rather than inferred from complaints.
 */
let hasReportedStorageRefusal = false;

const reportStorageRefusal = (operation: string): void => {
  if (hasReportedStorageRefusal) return;
  hasReportedStorageRefusal = true;
  logDataDogEvent({
    context: { key: VERIFY_LINK_ARRIVAL_KEY, operation },
    level: 'warn',
    message: 'verify-identity arrival flag: sessionStorage unavailable'
  });
};

export const markVerifyLinkArrival = (brazeExternalId = ''): void => {
  try {
    window.sessionStorage?.setItem(VERIFY_LINK_ARRIVAL_KEY, arrivalValue(brazeExternalId));
  } catch {
    reportStorageRefusal('set');
  }
};

/**
 * Whether this tab reached `/verify-identity` on a link *for this patient*.
 *
 * `'1'` is accepted as a wildcard so a tab that loaded an older bundle and then this one keeps its
 * suppression. Erring that way costs a patient one tap on Resend; erring the other way deletes a
 * code they are holding.
 */
export const hasVerifyLinkArrival = (brazeExternalId = ''): boolean => {
  try {
    const stored = window.sessionStorage?.getItem(VERIFY_LINK_ARRIVAL_KEY);
    if (stored === null || stored === undefined) return false;
    return stored === '1' || stored === arrivalValue(brazeExternalId);
  } catch {
    reportStorageRefusal('get');
    return false;
  }
};

/**
 * Forgets the arrival, once it cannot cost anything.
 *
 * Called when the link has minted a session or a typed code has been accepted — past either, the
 * code in the email has done its job and a later visit to this page is a genuinely new request
 * that should be allowed to send one.
 *
 * Deliberately *not* called on a terminal link failure. A patient looking at "This link has
 * already been used" is about to be shown the code form, and the code in their email may still be
 * live; clearing here would let a remount auto-request and delete it.
 */
export const clearVerifyLinkArrival = (): void => {
  try {
    window.sessionStorage?.removeItem(VERIFY_LINK_ARRIVAL_KEY);
  } catch {
    reportStorageRefusal('remove');
  }
};

let capturedToken = '';
let hasCaptured = false;

/**
 * Where the inline script at the top of `index.html` leaves the token.
 *
 * That script exists because this module is not early enough. The bundle is `type="module"` and
 * therefore deferred until the document has been parsed, so `index.html`'s own scripts — Everflow
 * blocking, ProfitWell async — read `location.href` first, and no amount of redaction inside our
 * telemetry reaches a third-party vendor.
 */
export const VERIFY_LINK_GLOBAL_KEY = '__verifyLinkToken';

/**
 * Takes the token off `window`, once.
 *
 * Deleted as it is read: the whole point of capturing earlier is to shorten the window in which a
 * live credential is reachable from the page, and leaving it on `window` would hand that back.
 */
/** The id the arrival is keyed to, read from the query the link always carries. */
const readBrazeExternalId = (): string => {
  try {
    return new URLSearchParams(window.location?.search ?? '').get('brazeExternalId')?.trim() ?? '';
  } catch {
    return '';
  }
};

const readHandoffToken = (): string => {
  const globals = window as unknown as Record<string, unknown>;
  const handoff = globals[VERIFY_LINK_GLOBAL_KEY];
  if (typeof handoff !== 'string') return '';
  delete globals[VERIFY_LINK_GLOBAL_KEY];
  return handoff.trim();
};

/**
 * Reads the token into memory and takes it out of the address bar, once per page load.
 *
 * Keeping the token out of the request line was never the whole problem. Our own client-side
 * telemetry reads `location.href` straight off the page: Datadog RUM copies it into the first
 * view's `view.url` the moment `init()` runs and into the next view's `view.referrer` after that,
 * PostHog stores it as `$session_entry_url` and rides it on every event for the rest of the
 * session, and GTM's page tags see it too. A fragment no server receives is still a credential
 * three vendors have on file.
 *
 * So this runs from `src/index.tsx` **before any SDK is initialised**, not from the page that
 * needs the token — that page is lazily routed and mounts long after the damage is done. The page
 * then calls the same function from its `useState` initialiser; the latch makes that a no-op
 * returning the already-captured value, which is also what makes it correct under StrictMode's
 * double-invoked initialiser and in tests, where `src/index.tsx` never runs.
 *
 * Redacting `vt` inside each telemetry pipeline (utils/datadog, hooks/useAppAnalytics) is the
 * other half. Neither half is sufficient alone: redaction cannot reach a Session Replay snapshot,
 * and clearing cannot undo a `location.href` that something read first.
 */
export const captureVerifyLinkToken = (): string => {
  if (hasCaptured) return capturedToken;
  hasCaptured = true;

  // The handoff first, because by the time this runs `index.html` has normally already cleared the
  // fragment. Reading the hash is the fallback for a load where that script did not run — tests,
  // which never execute `index.html`, and any environment that strips inline scripts. The query
  // is the last resort, for a link that reached the patient mangled.
  capturedToken =
    readHandoffToken() ||
    readVerifyLinkToken(window.location?.hash) ||
    readVerifyLinkTokenFromQuery(window.location?.search);
  if (capturedToken) {
    markVerifyLinkArrival(readBrazeExternalId());
    clearVerifyLinkFragment();
  }

  return capturedToken;
};

/**
 * Forgets the token's value while leaving the latch alone.
 *
 * The capture used to hold the token for the life of the document. `/verify-identity` can mount
 * more than once in one SPA session — an in-app navigation, or a guard sending the patient back —
 * and `useState(captureVerifyLinkToken)` would hand the remount the spent token. A fresh
 * `linkExchangeStartedRef` then POSTs it again, for a guaranteed `CONSUMED`: the patient reads
 * "This link has already been used" about a link that had just worked, and the attempt comes out
 * of their rate-limit budget.
 *
 * The latch stays set, which is the half that matters for correctness. Clearing it would send the
 * remount back to `window.location`, and under StrictMode's double-invoked initialiser the second
 * call would re-read a hash that may not have been cleared yet.
 */
export const markVerifyLinkTokenSpent = (): void => {
  capturedToken = '';
};

/**
 * Drops the latch so the next call re-reads `window.location`. Exists for tests: a real page load
 * captures exactly once, and nothing in the app should want a second capture.
 */
export const resetVerifyLinkTokenCapture = (): void => {
  capturedToken = '';
  hasCaptured = false;
  hasReportedStorageRefusal = false;
  delete (window as unknown as Record<string, unknown>)[VERIFY_LINK_GLOBAL_KEY];
  try {
    window.sessionStorage?.removeItem(VERIFY_LINK_ARRIVAL_KEY);
  } catch {
    /* empty */
  }
};
